// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import {
  Box,
  Button,
  Icon,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
  Typography
} from "@outschool/backpack";
import * as BusinessRules from "@outschool/business-rules";
import { CurrencyCode } from "@outschool/gql-frontend-generated";
import { faEllipsisV, faMessage, faQuestionCircle } from "@outschool/icons";
import {
  displayPerStudentPrice,
  displayPerStudentPriceCredits,
  renderCurrencyString,
  useLocale,
  useTranslation
} from "@outschool/localization";
import { enrolledPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import {
  IconButton,
  IconButtonProps
} from "@outschool/ui-legacy-component-library";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import * as Activity from "../../shared/Activity";
import * as Enrollment from "../../shared/Enrollment";
import useSectionString from "../../shared/hooks/useSectionString";
import * as Routes from "../../shared/Routes";
import { shortenedLeaderName } from "../../shared/User";
import { useParentUpcomingClassesQuery } from "../hooks/useParentUpcomingClassesQuery";
import { useTimeZone } from "../providers/TimeZoneProvider";
import { EnrollmentStatusChip } from "./Chips/EnrollmentStatusChip";
import { RemainingMeetingsChip } from "./Chips/RemainingMeetingsChip";
import { SectionStatusChip } from "./Chips/SectionStatusChip";
import {
  EnrollmentControl,
  getControlsToDisplay
} from "./enrolled/EnrollmentLine";
import ParentPauseOngoingButton from "./enrolled/ParentPauseOngoingButton";
import ParentTransferButton from "./enrolled/ParentTransferButton";
import ParentWithdrawButton from "./enrolled/ParentWithdrawButton";
import { ResumeOngoingEnrollment } from "./enrolling/ResumeOngoingEnrollment";
import Link from "./Link";
import LoadingWithMessage from "./LoadingWithMessage";
import {
  ClassDescriptionProps,
  ClassImage,
  ClassListInfoProps,
  ClassProps,
  ClassTeacher,
  EnrollmentType,
  SectionType,
  Seperator
} from "./loggedInHome/LoggedInHomeMyClassesList";
// @ts-ignore - JSX import
import MessageModal from "./MessageModal";

function getNumRemainingMeetings(section: SectionType) {
  return section.meetings.filter(
    (m: $TSFixMe) => !m.deleted_at && dayjs(m.end_time).isAfter()
  ).length;
}

const UpcomingClassDescription = ({
  enrollment,
  learner,
  section
}: ClassDescriptionProps) => {
  const { activity, nextOngoingMeeting } = section;
  const { timeZone } = useTimeZone();
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");
  const isFlexSchedule = Activity.isFlexSchedule(activity);
  const isOneTime = BusinessRules.isLiveOneTime(activity);
  const { fullDurationString } = useSectionString();
  const durationString = activity.isClub
    ? t("Unlimited access")
    : fullDurationString({
        activity,
        section,
        nextOngoingMeeting,
        userTimeZone: timeZone,
        showTimes: true
      });
  // do not show the duration string if the next meeting starts after the enrollment has ended
  const showDurationString =
    !enrollment.ongoingEndTime ||
    (nextOngoingMeeting &&
      dayjs(nextOngoingMeeting.start_time).isSameOrBefore(
        dayjs(enrollment?.ongoingEndTime)
      ));

  const numRemainingMeetings = getNumRemainingMeetings(section);

  const enrollmentPrice = BusinessRules.perStudentPriceInCents(
    activity,
    section,
    enrollment
  );
  const isRecurringFormat =
    activity.isClub ||
    activity.is_ongoing_weekly ||
    BusinessRules.isOneOnOneFormat(activity);
  const serviceFeePercentage =
    (isRecurringFormat
      ? enrollment.service_fee_percentage
      : enrollment.payments[0]?.service_fee_percentage) ?? 0;
  const totalPrice = BusinessRules.applyServiceFee(
    enrollmentPrice,
    serviceFeePercentage
  );
  const convertWithServiceFee = (priceCents: number) =>
    BusinessRules.applyServiceFee(priceCents, serviceFeePercentage);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6">{activity.title}</Typography>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          [theme.breakpoints.up("lg")]: {
            overflow: "hidden",
            whiteSpace: "nowrap"
          }
        })}
      >
        <Box flex sx={{ alignItems: "center", gap: 6 }}>
          <AvatarImage
            avatar={learner.avatar || DEFAULT_AVATAR}
            name={learner.name}
            sx={{ height: 24, width: 24 }}
          />
          <Typography variant="body1">{learner.name}</Typography>
          <EnrollmentStatusChip size="small" enrollment={enrollment} />
        </Box>
        {showDurationString && durationString && (
          <Box flex>
            <Seperator />
            <Typography variant="body1">{durationString}</Typography>
          </Box>
        )}
        <Box flex sx={{ marginRight: "1rem" }}>
          <Seperator />
          <Typography variant="body1">
            {currentUser?.canPayWithSubscriptionCredits
              ? displayPerStudentPriceCredits(activity, section, enrollment)
              : displayPerStudentPrice(
                  activity,
                  section,
                  enrollment,
                  undefined,
                  serviceFeePercentage ? convertWithServiceFee : undefined
                )}
          </Typography>
        </Box>
        {!!serviceFeePercentage &&
          !currentUser?.canPayWithSubscriptionCredits && (
            <PriceBreakdownWithServiceFee
              enrollmentPrice={enrollmentPrice}
              totalPrice={totalPrice}
              currencyCode={enrollment.currency_code}
            />
          )}
        <Box flex gap="0.25em">
          <SectionStatusChip size="small" section={section} />
          {!isFlexSchedule && !isOneTime && numRemainingMeetings <= 3 && (
            <RemainingMeetingsChip
              size="small"
              numRemainingMeetings={numRemainingMeetings}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

function PriceBreakdownWithServiceFee({
  enrollmentPrice,
  totalPrice,
  currencyCode
}: {
  enrollmentPrice: number;
  totalPrice: number;
  currencyCode: CurrencyCode;
}) {
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");
  return (
    <Tooltip
      title={
        <Box flex sx={{ flexDirection: "column" }}>
          <Typography variant="subtitle1">{t("Price Breakdown")}</Typography>
          <Typography variant="body2">
            {t("Total: {{totalPriceString}}", {
              totalPriceString: renderCurrencyString({
                priceInCents: totalPrice,
                showCurrencyCode: true,
                currencyCode
              })
            })}
          </Typography>
          <Typography variant="body2">
            {t("Class Fee: {{enrollmentPriceString}}", {
              enrollmentPriceString: renderCurrencyString({
                priceInCents: enrollmentPrice,
                showCurrencyCode: true,
                currencyCode
              })
            })}
          </Typography>
          <Typography variant="body2">
            {t("Service Fee: {{serviceFeePriceString}}", {
              serviceFeePriceString: renderCurrencyString({
                priceInCents: totalPrice - enrollmentPrice,
                showCurrencyCode: true,
                currencyCode
              })
            })}
          </Typography>
        </Box>
      }
    >
      <Icon
        icon={faQuestionCircle}
        color="primary"
        sx={{ marginLeft: "0.25em", marginRight: "0.25em" }}
      />
    </Tooltip>
  );
}

// This is gross but we need the Buttons in the overflow menu to match the style of the MenuItem
const BUTTON_INHERIT_SX: SxProps = {
  width: "100%",
  color: "inherit",
  textDecoration: "none !important",
  fontSize: "inherit",
  backgroundColor: "transparent",
  "&:hover:not(:disabled), &:focus:not(:disabled)": {
    // Match default MenuItem hover color
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    color: "inherit !important"
  }
};

interface OverflowMenuButtonProps
  extends Omit<IconButtonProps, "icon" | "onClick"> {
  enrollment: EnrollmentType;
}

const OverflowMenuButton = ({
  enrollment,
  ...iconButtonProps
}: OverflowMenuButtonProps) => {
  const locale = useLocale();
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [pauseOpen, setPauseOpen] = React.useState(false);
  const [withdrawOpen, setWithdrawOpen] = React.useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Stop event from bubbling to link underneath
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    // stop close event from propogating click event to link underneath
    event.stopPropagation();
    setAnchorEl(null);
  };

  const { section } = enrollment;
  const { activity } = section;

  const menuItems: React.ReactNode[] = [];

  if (activity.hasTeacherSchedule) {
    // New 1:1 format class
    menuItems.push(
      <MenuItem
        component={Link}
        to={enrolledPath(section.uid, locale)}
        sx={BUTTON_INHERIT_SX}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
        }}
        key="manage-meetings"
      >
        {t("Manage meetings")}
      </MenuItem>
    );
  } else {
    const controlsToDisplay = getControlsToDisplay(enrollment, activity);
    controlsToDisplay.forEach((control: EnrollmentControl) => {
      switch (control) {
        case EnrollmentControl.Resume:
          menuItems.push(
            <MenuItem
              component={ResumeOngoingEnrollment as any}
              variant="link"
              color="inherit"
              sx={BUTTON_INHERIT_SX}
              enrollment={enrollment}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault();
              }}
              key="resume-ongoing"
            />
          );
          break;
        case EnrollmentControl.WithdrawOrStop:
          menuItems.push(
            <MenuItem
              component={ParentWithdrawButton as any}
              variant="link"
              color="inherit"
              sx={BUTTON_INHERIT_SX}
              section={section}
              activity={activity}
              enrollment={enrollment}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault();
              }}
              open={withdrawOpen}
              setOpen={setWithdrawOpen}
              key="withdraw"
            />
          );
          break;
        case EnrollmentControl.Pause:
          menuItems.push(
            <MenuItem
              component={ParentPauseOngoingButton as any}
              variant="link"
              color="inherit"
              sx={BUTTON_INHERIT_SX}
              activity={activity}
              section={section}
              enrollment={enrollment}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault();
              }}
              open={pauseOpen}
              setOpen={setPauseOpen}
              key="pause-ongoing"
            />
          );
          break;
        case EnrollmentControl.RequestTransfer:
          menuItems.push(
            <MenuItem
              component={ParentTransferButton as any}
              variant="link"
              color="inherit"
              sx={BUTTON_INHERIT_SX}
              activity={activity}
              section={section}
              enrollment={enrollment}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault();
              }}
              key="transfer"
            />
          );
          break;
      }
    });
  }

  return (
    <>
      <IconButton
        icon={faEllipsisV}
        onClick={handleClick}
        {...iconButtonProps}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        {menuItems}
      </Menu>
    </>
  );
};

interface MessageTeacherButtonProps {
  enrollment: NonNullable<EnrollmentType>;
}

const MessageTeacherButton = ({ enrollment }: MessageTeacherButtonProps) => {
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");
  const location = useLocation();
  const { currentUser } = useSession();

  const { section } = enrollment;
  const { activity, leader } = section;

  return (
    <MessageModal
      renderButton={(open: $TSFixMe) => (
        <IconButton icon={faMessage} onClick={open} />
      )}
      user={currentUser}
      location={location}
      requireLogin={true}
      isLoggedIn
      viewName="messageToLeader"
      heading={t("Message to {{teacherName}}", {
        teacherName: shortenedLeaderName(leader.name)
      })}
      recipients={[leader]}
      activityUid={activity.uid}
      sectionUid={section && section.uid}
      showFromAndTo
      subject={t("Message about: {{activityTitle}}", {
        activityTitle: activity.title
      })}
      context={{
        action: "Enrolled parent to teacher",
        activityTitle: activity.title,
        subject: "Message about: " + activity.title
      }}
    />
  );
};

interface UpcomingClassActionProps {
  enrollment: EnrollmentType;
}

const UpcomingClassActions = ({ enrollment }: UpcomingClassActionProps) => (
  <Box
    flex
    sx={{
      alignItems: "center"
    }}
  >
    <MessageTeacherButton enrollment={enrollment} />
    <OverflowMenuButton
      enrollment={enrollment}
      disabled={enrollment.isEnded || Enrollment.isWithdrawn(enrollment)}
    />
  </Box>
);

const UpcomingClass = ({ enrollment, sx: sxProps }: ClassProps) => {
  const locale = useLocale();

  const { section, learner } = enrollment;
  const { activity, leader: sectionLeader } = section;

  const { details, leader: activityLeader } = activity;
  const { photo } = details;

  // Favor section leaders in the event it is a seller org
  const leader = sectionLeader || activityLeader;

  if (!learner) {
    return null;
  }

  return (
    <Box
      flex
      sx={[
        (theme: Theme) => ({
          paddingY: 16,
          paddingX: 24,
          gap: 8,
          borderColor: "grey.100",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          justifyContent: "space-between",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            gap: 0
          },
          // reset link styles
          textDecoration: "none",
          color: "unset",
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              textDecoration: "none",
              color: "unset"
            }
        }),
        ...(Array.isArray(sxProps) ? sxProps : [sxProps])
      ]}
      component={Link}
      to={enrolledPath(section.uid, locale)}
    >
      <Box
        flex
        sx={{
          gap: 24,
          alignItems: "center",
          flexDirection: "row",
          flexWrap: "wrap"
        }}
      >
        <ClassImage
          src={photo ? photo : ""}
          sx={{
            width: 93,
            height: 88
          }}
        />
        <UpcomingClassDescription
          enrollment={enrollment}
          learner={learner}
          section={section}
        />
      </Box>
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: 16,
          alignItems: "center",
          justifyContent: "space-between",
          [theme.breakpoints.up("md")]: {
            justifyContent: "flex-start"
          }
        })}
      >
        <ClassTeacher leader={leader} isLinkComponent={false} />
        <UpcomingClassActions enrollment={enrollment} />
      </Box>
    </Box>
  );
};

interface UpcomingClassesListProps {
  limitUpcomingClasses?: number | undefined;
  sx?: SxProps;
  listItemSx?: SxProps;
  handleClassListInfo?: (info: ClassListInfoProps) => void;
  filterByLearnerUid?: string | null;
  includeNoMeetingsClasses?: boolean;
}

export const UpcomingClassesList = ({
  limitUpcomingClasses,
  sx,
  listItemSx,
  handleClassListInfo,
  filterByLearnerUid,
  includeNoMeetingsClasses = false
}: UpcomingClassesListProps) => {
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");
  const navigate = useNavigation();
  const { error, loading, upcomingEnrollments, renderedEnrollments } =
    useParentUpcomingClassesQuery({
      limitUpcomingClasses,
      filterByLearnerUid,
      onCompleted: handleClassListInfo,
      includeNoMeetingsClasses
    });

  return (
    <Box
      flex
      sx={[
        { flexDirection: "column", gap: 24 },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {/* Don't show "Loading..." if the caller conditionally renders a title, since this can cause a UI 'flash' */}
      {loading && !handleClassListInfo ? (
        <LoadingWithMessage>{t("Loading…")}</LoadingWithMessage>
      ) : !loading && renderedEnrollments.length === 0 ? (
        <Typography variant="body1" component="div">
          {!!error
            ? t("Oops, we encountered an error when loading your enrollments.")
            : t("You have no upcoming classes or tutoring sessions.")}
        </Typography>
      ) : (
        !loading &&
        renderedEnrollments.length > 0 &&
        renderedEnrollments.map(enrollment => (
          <UpcomingClass
            // @ts-ignore TS(2531): Object is possibly 'null'.
            key={enrollment.uid}
            enrollment={enrollment}
            sx={listItemSx}
          />
        ))
      )}
      {limitUpcomingClasses != null &&
        limitUpcomingClasses < upcomingEnrollments.length && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Button
              variant={"outlined"}
              onClick={() => navigate(Routes.learnScheduleUrl())}
            >
              {t("View All")}
            </Button>
          </Box>
        )}
    </Box>
  );
};
